import { useState } from "react";
import { Link as RouterLink } from "react-router";

import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { Button, Divider, List, ListItem, Stack, Typography } from "@mui/material";
import { DropZoneUploadButtonSize, FileUploadArea } from "@shared";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { AssetInventoryDocumentResponse, documentsApi } from "@/api";
import { BulkActionBar } from "@/common/components/layout/components/dropzone/components/bulk-action-bar/BulkActionBar";
import { MergeFilesDialog } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/MergeFilesDialog";
import { useMergeFiles } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/hooks/useMergeFiles";
import {
  PendingDocumentMove,
  getIncomingMoves,
  getOutgoingDocumentIds,
} from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { PendingMoveRow } from "@/common/components/layout/components/dropzone/dnd/components/pending-move-row/PendingMoveRow";
import { usePendingMoveZone } from "@/common/components/layout/components/dropzone/dnd/hooks/usePendingMoveZone";
import { fetchClientDocumentBlob } from "@/common/components/layout/components/dropzone/hooks/dropzoneFetch.utils";
import { useDocumentBulkActions } from "@/common/components/layout/components/dropzone/hooks/useDocumentBulkActions";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { AssetInventoryUploadArea } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/assetInventoryUploadAreas";
import { AssetDocumentDropArea } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/components/asset-document-drop-area/AssetDocumentDropArea";
import { AssetInventoryDocumentRow } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/components/asset-inventory-document-row/AssetInventoryDocumentRow";
import { toDocumentResponse } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/components/asset-inventory-document-row/AssetInventoryDocumentRow.utils";
import { useAssetInventoryDocumentActions } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/hooks/useAssetInventoryDocumentActions";
import { clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

const OVERVIEW_SECTION = "clients.client.debtSettlement.assetInventory.documentsOverview";

interface Props {
  clientId: string;
  area: AssetInventoryUploadArea;
  hint?: string;
  pendingFiles: File[];
  documentMoves: PendingDocumentMove[];
  onDropFiles: (files: File[]) => void;
  onRemovePending: (index: number) => void;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
  /** Adds per-row checkboxes and the bulk merge/download/delete bar. */
  enableSelection?: boolean;
  /** Appends the saved + pending document count to the title. */
  showCount?: boolean;
  dense?: boolean;
  /** Leaves out the title above the drop target. */
  hideTitle?: boolean;
  uploadButtonSize?: DropZoneUploadButtonSize;
  uploadButtonLabel?: string;
  gap?: number;
}

/**
 * One asset inventory upload area: drop target, saved document rows, pending uploads and
 * pending moves — shared by the owning sections (Persönliche Daten, Angehörige) and the
 * documents overview, which additionally enables multi-select with the DropZone bulk actions.
 * Uploads and drag-moves are deferred: they live in the owning view's Formik state and are
 * persisted on save.
 */
export function AssetDocumentSection({
  clientId,
  area,
  hint,
  pendingFiles,
  documentMoves,
  onDropFiles,
  onRemovePending,
  onAddMove,
  onCancelMove,
  enableSelection = false,
  showCount = false,
  dense = false,
  hideTitle = false,
  uploadButtonSize,
  uploadButtonLabel,
  gap = 2,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const listQueryKey = [area.listQueryKey, clientId];

  const { data: documents = [] } = useQuery({
    queryKey: listQueryKey,
    queryFn: () => area.listDocuments(clientId),
  });

  const actions = useAssetInventoryDocumentActions({ clientId, source: area.group, listQueryKey });

  usePendingMoveZone({
    zone: area.group,
    moves: documentMoves,
    onAddMove,
    onRemoveMove: onCancelMove,
  });

  const incomingMoves = getIncomingMoves(documentMoves, area.group);
  const outgoingIds = getOutgoingDocumentIds(documentMoves, area.group);
  const visibleDocuments = documents.filter((document) => !outgoingIds.has(document.documentId));

  const bulk = useDocumentBulkActions<AssetInventoryDocumentResponse>({
    files: visibleDocuments,
    deleteFn: (docs) => documentsApi.deleteDocuments({ clientId, ids: docs.map((d) => d.documentId) }),
    fetchDownloadBlobFn: (doc) => fetchClientDocumentBlob(clientId, doc.documentId),
    invalidationKeys: [listQueryKey],
  });

  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const selectedDocuments = visibleDocuments.filter((d) => bulk.selectedFiles.has(d.documentId));

  const mergeMutation = useMergeFiles({
    mergeFn: (orderedItems) =>
      documentsApi.mergeDocuments({
        clientId,
        mergeDocumentsRequest: {
          items: orderedItems.map((it) => ({ documentId: it.fileId, rotationDegrees: it.rotationDegrees })),
        },
      }),
    invalidationKeys: [listQueryKey],
    onSuccess: () => setIsMergeOpen(false),
  });

  const resolvedTitle = t(area.titleKey);
  const displayTitle = showCount
    ? t(`${OVERVIEW_SECTION}.sectionTitle`, {
        title: resolvedTitle,
        count: visibleDocuments.length + incomingMoves.length + pendingFiles.length,
      })
    : resolvedTitle;

  const uploadArea = (
    <FileUploadArea
      title={dense || hideTitle ? undefined : displayTitle}
      hint={hint}
      onDropFiles={onDropFiles}
      pendingFiles={pendingFiles}
      onRemovePending={onRemovePending}
      uploadButtonSize={uploadButtonSize}
      uploadButtonLabel={uploadButtonLabel}
      dense={dense}
      gap={gap}
    >
      {visibleDocuments.length > 0 && (
        <List disablePadding sx={{ width: "100%" }}>
          {visibleDocuments.map((document) => (
            <ListItem key={document.documentId} disableGutters divider={dense}>
              <AssetInventoryDocumentRow
                document={document}
                sourceZone={area.group}
                actions={actions}
                dense={dense}
                secondaryText={format(document.createdAtUtc, "Pp", { locale: locales[language] })}
                menuAriaLabel={t(`${OVERVIEW_SECTION}.fileActions`, { fileName: document.fileName })}
                selected={enableSelection ? bulk.selectedFiles.has(document.documentId) : undefined}
                onToggleSelect={enableSelection ? bulk.handleToggleSelect : undefined}
              />
            </ListItem>
          ))}
        </List>
      )}
      {incomingMoves.map((move) => (
        <PendingMoveRow
          key={move.document.documentId}
          move={move}
          onCancel={() => onCancelMove(move.document.documentId)}
        />
      ))}
      {enableSelection && (
        <BulkActionBar
          selectedCount={bulk.selectedFiles.size}
          onDownload={bulk.handleBulkDownload}
          onDelete={() => bulk.handleDelete(selectedDocuments)}
          onMerge={() => setIsMergeOpen(true)}
          disabled={bulk.isBulkActionPending}
        />
      )}
    </FileUploadArea>
  );

  return (
    <AssetDocumentDropArea zoneId={area.group}>
      {dense ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ px: 2, py: 0.5 }}>
            <Typography variant="h6">{displayTitle}</Typography>
            <Button
              variant="text"
              size="small"
              component={RouterLink}
              to={clientPath.byAssetInventorySubTab({ id: clientId, subTab: area.owningSubTab })}
              startIcon={<ArrowOutwardRoundedIcon />}
              aria-label={t(`${OVERVIEW_SECTION}.goToSection`, { section: t(area.owningSectionLabelKey) })}
            >
              {t(area.owningSectionLabelKey)}
            </Button>
          </Stack>
          <Divider />
          <Stack sx={{ px: 2, pt: 1, pb: 2 }}>{uploadArea}</Stack>
        </>
      ) : (
        uploadArea
      )}
      {isMergeOpen && selectedDocuments.length >= 2 && (
        <MergeFilesDialog
          files={selectedDocuments.map(toDocumentResponse)}
          onCancel={() => setIsMergeOpen(false)}
          onConfirm={(orderedItems) => mergeMutation.mutate(orderedItems)}
          isSubmitting={mergeMutation.isPending}
          fetchImageBlob={(file) => fetchClientDocumentBlob(clientId, file.documentId)}
        />
      )}
    </AssetDocumentDropArea>
  );
}
